import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { parseHiBidCSV } from '../utils/hibidParser';
import { resolveConsignors, type ConsignorResolution, type ConsignorRecordLite } from '../utils/consignorResolve';
import { calculateAllLotEndTimes, DEFAULT_STAGGER_SECONDS } from '../utils/lotTiming';
import { syncAuctionCreated } from '../services/auctionringer.service';
import { DEFAULT_SCHEDULE } from '../services/bidding.service';
import { Auction, AuctionLot, BidIncrementBand, SaleInfo } from '../types/auction.types';
import toast from 'react-hot-toast';

// Schmidt Auctions' standard Sale Info & Terms — lifted VERBATIM from seed-sale-info.mjs (the
// content currently on the live sale). Operators edit these defaults rather than authoring from
// scratch. All 10 terms + 8 scalars.
const DEFAULT_SALE_INFO: SaleInfo = {
  auctionType: 'Online Only Auction',
  dateTimeInfo:
    'Sale starts closing at 8:00 PM Eastern. Each lot extends the clock for last-second bids. ' +
    'Lots close fast — use our Max Bid (auto-bid) system with confidence.',
  previewInfo:
    'We take many photos, so be sure to click into lots of interest. If we didn\'t catch an ' +
    'angle of something in particular, just reach out to let us know.',
  checkoutInfo: 'Invoices are processed after the sale. Look for your copy via email.',
  location: '416 Chestnut St, Pottstown, PA 19464',
  buyersPremiumText: '15% Credit Card / 12% Cash or Check',
  paymentTerms:
    'The credit card used to register will be charged at the conclusion of the sale. You are ' +
    'responsible to make arrangements during the scheduled times to pick up your items. ' +
    'In-house shipping available.',
  shippingPickup:
    'LOCAL PICKUP by appointment. IN-HOUSE SHIPPING AVAILABLE. Chestnut Street location. ' +
    'Call/Text 484-366-1780 with any questions.',
  terms: [
    'Bidders are approved for online bidding upon registration, which requires a valid credit ' +
      'card. A $0 authorization is placed on the card at registration to verify it; the card on ' +
      'file will be charged at the conclusion of the sale. The auction company has sole ' +
      'discretion to approve or deny any bidder.',
    'All items are sold "As-Is". All information contained in descriptions is believed to be ' +
      'accurate; however, no information provided is guaranteed. Schmidt Auctions and its ' +
      'employees shall not be responsible for any inaccuracies. The bidder assumes all risks ' +
      'related to the grade, quality, appearance, condition, history, and origin of any lot.',
    'Bidders acknowledge a 15% Credit Card / 12% Cash-or-Check Buyer\'s Premium will be added ' +
      'to all winning bids and is payable by the winning bidder as part of the total cost.',
    'All sales are subject to 6% PA Sales Tax unless a valid Tax Exemption Form is on file with ' +
      'Schmidt Auctions. Please email any Tax Exemption Forms to dan@schmidtauctions.com.',
    'All items MUST be paid for within 48 hours of the sale closing. Any lots that are unpaid ' +
      'for, or paid for and not picked up, or shipping charges not paid for, will be considered ' +
      'abandoned property and ownership reverts back to the auctioneer.',
    'All sales are final. Lots may not be returned or refunded for ANY reason. Schmidt Auctions ' +
      'assumes no responsibility for any items once sold.',
    'In-House Shipping is available when designated; you pay actual postage plus a $5 handling ' +
      'charge per box for reasonably sized orders. Orders with large quantities of lots, or lots ' +
      'that are excessively large, heavy, or bulky, we reserve the right to use a third party ' +
      'shipping service such as UPS at the expense of the winning bidder.',
    'Lots close on a staggered schedule. Any bid placed in the final moments of a lot extends ' +
      'that lot\'s clock, so bidding continues until no further bids are placed. Lots close ' +
      'fast — use the Max Bid system.',
    'You may set a Max Bid (proxy bid). The system will bid on your behalf only as much as ' +
      'needed to keep you in the lead, up to your maximum — it does not disclose or spend your ' +
      'maximum unless required by competing bids. You may raise your maximum at any time, ' +
      'including while you are the high bidder.',
    'Bids must adhere to the published bid increment schedule (see Bid Increments).',
  ],
};

const ImportCSV: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsedLots, setParsedLots] = useState<AuctionLot[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [imageFolders, setImageFolders] = useState<string[]>([]);
  const [consignorCheck, setConsignorCheck] = useState<ConsignorResolution | null>(null);

  /**
   * Read the live consignor records and resolve every lot's Consignor NUMBER to one
   * consignors/{uid}. Called at parse (to show it) AND at import (the guard). A read failure
   * resolves against zero records, which refuses any attributed file rather than guessing.
   */
  const checkConsignors = async (lots: AuctionLot[]): Promise<ConsignorResolution> => {
    let records: ConsignorRecordLite[] = [];
    if (lots.some((l) => String(l.consignor ?? '').trim())) {
      try {
        const snap = await getDocs(collection(db, 'consignors'));
        records = snap.docs.map((d) => ({
          id: d.id,
          consignorNumber: d.data().consignorNumber ?? null,
          legacyCode: d.data().legacyCode ?? null,
          name: d.data().name ?? ([d.data().firstName, d.data().lastName].filter(Boolean).join(' ') || null),
          mergedInto: d.data().mergedInto ?? null,
        }));
      } catch (err) {
        console.error('Could not read consignor records:', err);
      }
    }
    return resolveConsignors(lots, records);
  };
  
  const [auctionTitle, setAuctionTitle] = useState('');
  const [auctionDescription, setAuctionDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');

  // Per-event bid-increment schedule. Defaults to the Schmidt/HiBid DEFAULT_SCHEDULE;
  // an operator can override it via the schedule editor in the import form (see the
  // "Bid Increments" section of the JSX). null override => seed the default.
  const [bidIncrementsOverride, setBidIncrementsOverride] = useState<BidIncrementBand[] | null>(null);

  // Sale Info & Terms — seeded with Schmidt's standard content so the operator edits, not authors.
  const [saleInfoForm, setSaleInfoForm] = useState({
    auctionType: DEFAULT_SALE_INFO.auctionType,
    dateTimeInfo: DEFAULT_SALE_INFO.dateTimeInfo,
    previewInfo: DEFAULT_SALE_INFO.previewInfo,
    checkoutInfo: DEFAULT_SALE_INFO.checkoutInfo,
    location: DEFAULT_SALE_INFO.location,
    buyersPremiumText: DEFAULT_SALE_INFO.buyersPremiumText,
    paymentTerms: DEFAULT_SALE_INFO.paymentTerms,
    shippingPickup: DEFAULT_SALE_INFO.shippingPickup,
  });
  const [terms, setTerms] = useState<string[]>(DEFAULT_SALE_INFO.terms ?? []);
  const setSI = (k: keyof typeof saleInfoForm, v: string) => setSaleInfoForm((s) => ({ ...s, [k]: v }));
  const updateTerm = (i: number, v: string) => setTerms((t) => t.map((x, j) => (j === i ? v : x)));
  const addTerm = () => setTerms((t) => [...t, '']);
  const removeTerm = (i: number) => setTerms((t) => t.filter((_, j) => j !== i));
  const moveTerm = (i: number, dir: -1 | 1) => setTerms((t) => {
    const j = i + dir;
    if (j < 0 || j >= t.length) return t;
    const n = [...t];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const [importing, setImporting] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setParsing(true);
    setParsedLots([]);
    setParseErrors([]);
    
    try {
      const text = await selectedFile.text();
      const result = parseHiBidCSV(text);
      
      setParsedLots(result.lots);
      setParseErrors(result.errors);
      setImageFolders(Array.from(result.imageFolders));
      setConsignorCheck(await checkConsignors(result.lots));
      
      if (result.lots.length > 0) {
        toast.success(`Parsed ${result.lots.length} lots successfully!`);
        
        // Auto-generate auction title from filename
        const filename = selectedFile.name.replace('.csv', '').replace(/_/g, ' ');
        setAuctionTitle(filename);
      }
      
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} errors found - check console`);
        console.error('Parse errors:', result.errors);
      }
      
    } catch (error) {
      toast.error('Failed to parse CSV file');
      console.error(error);
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    console.log('🔵 IMPORT STARTED');
    console.log('userProfile:', userProfile);
    console.log('parsedLots.length:', parsedLots.length);
    console.log('auctionTitle:', auctionTitle);
    console.log('startDate:', startDate);
    console.log('endDate:', endDate);
    
    if (!userProfile) {
      console.log('❌ FAILED: No userProfile');
      return;
    }
    if (parsedLots.length === 0) {
      console.log('❌ FAILED: No lots');
      toast.error('No lots to import');
      return;
    }
    if (!auctionTitle || !startDate || !endDate) {
      console.log('❌ FAILED: Missing fields', { auctionTitle, startDate, endDate });
      toast.error('Please fill in auction details');
      return;
    }
    
    // ── CONSIGNOR GUARD — resolved again here, against the records as they are NOW ──
    const consignors = await checkConsignors(parsedLots);
    setConsignorCheck(consignors);
    if (!consignors.ok) {
      toast.error(`Import refused — ${consignors.errors.length} consignor problem(s). Nothing was created.`);
      return;
    }

    console.log('✅ VALIDATION PASSED');

    setImporting(true);
    
    try {
      // Helper function to remove undefined values
      const cleanObject = (obj: any) => {
        return Object.fromEntries(
          Object.entries(obj).filter(([_, v]) => v !== undefined)
        );
      };

      // Compute lot end times at import so lots are never "no end time -> CLOSED".
      // scheduledEndTime = endDate + index*staggerTime; actualEndTime seeds equal
      // (soft-close extends it later). Reuses lotTiming.ts — no duplicated math.
      const staggerTime = DEFAULT_STAGGER_SECONDS;
      const lotsWithEndTimes = calculateAllLotEndTimes({
        endDate: new Date(endDate),
        staggerTime,
        lots: parsedLots,
      } as Auction);

      // Create base auction object
      const baseAuction = {
        auctionHouseId: userProfile.auctionHouseId,
        auctionHouseName: userProfile.auctionHouseName,
        title: auctionTitle,
        startDate: Timestamp.fromDate(new Date(startDate)),
        endDate: Timestamp.fromDate(new Date(endDate)),
        status: 'draft',
        visibility: 'network',
        staggerTime,
        // Seed the bid-increment schedule onto the auction so the client and the proxy
        // engine both read ONE source. Operator override if provided, else the default.
        bidIncrements: bidIncrementsOverride ?? DEFAULT_SCHEDULE,
        allowCrossPlatformPredictions: true,
        totalLots: parsedLots.length,
        importedFrom: 'hibid',
        importedAt: Timestamp.fromDate(new Date()),
        syncToAuctionRinger: true,
        syncStatus: 'pending',
        createdBy: userProfile.uid,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      // Add optional fields only if they have values
      const auctionData: any = { ...baseAuction };
      
      if (auctionDescription && auctionDescription.trim()) {
        auctionData.description = auctionDescription;
      }
      
      if (location && location.trim()) {
        auctionData.location = location;
      }

      // Sale Info & Terms — OMIT empty fields (SaleInfo.tsx omits empty rows/sections, so
      // writing '' would only add noise). Only attach saleInfo if something is populated.
      const cleanSaleInfo: any = {};
      (Object.keys(saleInfoForm) as (keyof typeof saleInfoForm)[]).forEach((k) => {
        const v = saleInfoForm[k]?.trim();
        if (v) cleanSaleInfo[k] = v;
      });
      const cleanTerms = terms.map((t) => t.trim()).filter((t) => t.length > 0);
      if (cleanTerms.length) cleanSaleInfo.terms = cleanTerms;
      if (Object.keys(cleanSaleInfo).length) auctionData.saleInfo = cleanSaleInfo;

      // Clean up lots - remove undefined values from each lot
      auctionData.lots = lotsWithEndTimes.map(lot => {
        const baseLot = {
          id: lot.id,
          sku: lot.sku,
          title: lot.title,
          description: lot.description,
          startingBid: lot.startingBid,
          category: lot.category,
          images: lot.images,
          currentBid: lot.currentBid || lot.startingBid,
          bidCount: lot.bidCount,
          status: lot.status,
          // End times computed above (endDate + index*staggerTime).
          scheduledEndTime: Timestamp.fromDate(new Date(lot.scheduledEndTime as any)),
          actualEndTime: Timestamp.fromDate(new Date(lot.actualEndTime as any)),
          extensionCount: lot.extensionCount ?? 0,
          createdAt: Timestamp.fromDate(lot.createdAt),
        };

        // Add optional fields only if they exist
        const cleanedLot: any = { ...baseLot };
        
        if (lot.reservePrice !== undefined && lot.reservePrice !== null) {
          cleanedLot.reservePrice = lot.reservePrice;
        }
        if (lot.tier) cleanedLot.tier = lot.tier;
        if (lot.estimatedValue) cleanedLot.estimatedValue = lot.estimatedValue;
        if (lot.expertLowEstimate !== undefined) cleanedLot.expertLowEstimate = lot.expertLowEstimate;
        if (lot.expertHighEstimate !== undefined) cleanedLot.expertHighEstimate = lot.expertHighEstimate;
        if (lot.condition) cleanedLot.condition = lot.condition;
        if (lot.year) cleanedLot.year = lot.year;
        if (lot.manufacturer) cleanedLot.manufacturer = lot.manufacturer;
        if (lot.measures) cleanedLot.measures = lot.measures;
        if (lot.hibidLotId) cleanedLot.hibidLotId = lot.hibidLotId;
        if (lot.consignor) {
          cleanedLot.consignor = String(lot.consignor).trim().toUpperCase();
          // Resolved at import (consignorResolve.ts) — no post-hoc code→uid script needed.
          const cid = consignors.byNumber[cleanedLot.consignor];
          if (cid) cleanedLot.consignorId = cid;
        }
        if (lot.notes) cleanedLot.notes = lot.notes;
        if (lot.updatedAt) cleanedLot.updatedAt = Timestamp.fromDate(lot.updatedAt);

        return cleanedLot;
      });
      
      console.log('📦 Cleaned auction data:', auctionData);
      
      // Save to Firestore
      const auctionRef = await addDoc(collection(db, 'auctions'), auctionData);
      
      toast.success('Auction created successfully!');
      
      // Sync to AuctionRinger
      const fullAuction: Auction = { 
        ...auctionData,
        id: auctionRef.id,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        createdAt: new Date(),
        updatedAt: new Date(),
        lots: parsedLots,
      };
      
      const syncResult = await syncAuctionCreated(fullAuction);
      
      if (syncResult.success) {
        toast.success('Synced to AuctionRinger!');
      } else {
        toast.error('Failed to sync to AuctionRinger - will retry later');
        console.error('Sync error:', syncResult.error);
      }
      
      // Navigate to auction details
      navigate(`/auctions/${auctionRef.id}`);
      
    } catch (error) {
      toast.error('Failed to create auction');
      console.error('❌ Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Import HiBid CSV</h1>
        <p className="text-gray-600 mt-1">
          Upload your HiBid auction catalog to create a new auction
        </p>
      </div>

      {/* File Upload */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">1. Upload CSV File</h2>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload"
            disabled={parsing || importing}
          />
          <label htmlFor="csv-upload" className="cursor-pointer">
            {file ? (
              <div>
                <p className="text-lg font-semibold text-primary-600">{file.name}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {parsedLots.length} lots parsed
                </p>
                <button className="btn-secondary mt-4" disabled={parsing || importing}>
                  Choose Different File
                </button>
              </div>
            ) : (
              <div>
                <div className="text-4xl mb-4">📤</div>
                <p className="text-lg font-semibold">Click to upload CSV file</p>
                <p className="text-sm text-gray-600 mt-2">
                  HiBid format expected
                </p>
              </div>
            )}
          </label>
        </div>
        
        {parsing && (
          <p className="text-center text-gray-600 mt-4">Parsing CSV...</p>
        )}
        
        {parseErrors.length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="font-semibold text-red-800 mb-2">
              {parseErrors.length} errors found:
            </p>
            <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
              {parseErrors.map((error, i) => (
                <li key={i}>• {error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {consignorCheck && (consignorCheck.lotsExamined > 0) && (
        <div className={`card ${consignorCheck.ok ? 'border-green-200' : 'border-red-300 bg-red-50'}`}>
          <p className="font-semibold">
            Consignors: {consignorCheck.lotsExamined} lot(s) examined · {consignorCheck.lotsAttributed} attributed ·{' '}
            {consignorCheck.lotsBlank} with no consignor · {consignorCheck.recordsExamined} record(s) checked
            {consignorCheck.ok ? '' : ' — IMPORT REFUSED'}
          </p>
          {Object.entries(consignorCheck.byNumber).map(([num, cid]) => (
            <p key={num} className="text-sm text-gray-700">{num} → consignors/{cid}</p>
          ))}
          {consignorCheck.errors.map((e, i) => (
            <p key={i} className="text-sm text-red-700">• {e}</p>
          ))}
          {consignorCheck.lotsBlank > 0 && (
            <p className="text-sm text-amber-700 mt-1">
              {consignorCheck.lotsBlank} lot(s) carry no consignor and will import uncredited.
            </p>
          )}
        </div>
      )}

      {/* Auction Details Form */}
      {parsedLots.length > 0 && (
        <>
          <div className="card">
            <h2 className="text-xl font-bold mb-4">2. Auction Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="label">Auction Title *</label>
                <input
                  type="text"
                  value={auctionTitle}
                  onChange={(e) => setAuctionTitle(e.target.value)}
                  className="input"
                  placeholder="January 2026 Forward Foundation Sale"
                  required
                  disabled={importing}
                />
              </div>
              
              <div>
                <label className="label">Description</label>
                <textarea
                  value={auctionDescription}
                  onChange={(e) => setAuctionDescription(e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Optional auction description..."
                  disabled={importing}
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="input"
                    required
                    disabled={importing}
                  />
                </div>
                
                <div>
                  <label className="label">End Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="input"
                    required
                    disabled={importing}
                  />
                </div>
              </div>
              
              <div>
                <label className="label">Location</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="input"
                  placeholder="Southampton, PA"
                  disabled={importing}
                />
              </div>
            </div>

            {/* Bid Increments — operator override. Defaults to the Schmidt/HiBid
                DEFAULT_SCHEDULE; editing any row switches this auction to a custom
                schedule (seeded onto auction.bidIncrements, read by BOTH the client
                and the proxy engine). "Reset to default" clears the override. */}
            <div className="mt-6 border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">
                  Bid Increments{' '}
                  <span className="text-xs text-gray-500">
                    ({bidIncrementsOverride ? 'custom' : 'default schedule'})
                  </span>
                </label>
                {bidIncrementsOverride && (
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setBidIncrementsOverride(null)}
                    disabled={importing}
                  >
                    Reset to default
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {(bidIncrementsOverride ?? DEFAULT_SCHEDULE).map((band, i) => {
                  const schedule = bidIncrementsOverride ?? DEFAULT_SCHEDULE;
                  const editRow = (patch: Partial<BidIncrementBand>) => {
                    const next = schedule.map((b, j) => (j === i ? { ...b, ...patch } : b));
                    setBidIncrementsOverride(next);
                  };
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500 w-24">up to ($)</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input py-1 w-32"
                        placeholder="∞ (top band)"
                        value={band.upTo == null ? '' : (band.upTo / 100).toFixed(2)}
                        onChange={(e) =>
                          editRow({
                            upTo: e.target.value === '' ? null : Math.round(parseFloat(e.target.value) * 100),
                          })
                        }
                        disabled={importing}
                      />
                      <span className="text-gray-500">→ increment ($)</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input py-1 w-32"
                        value={(band.increment / 100).toFixed(2)}
                        onChange={(e) =>
                          editRow({ increment: Math.round(parseFloat(e.target.value || '0') * 100) })
                        }
                        disabled={importing}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                CENTS on the doc; shown here in dollars. Bands must ascend by "up to"; the
                last band is open-ended (blank = ∞). Leave untouched to seed the default.
              </p>
            </div>
          </div>

          {/* Sale Info & Terms — public /sale/<id>/info content */}
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Sale Info &amp; Terms</h2>
            <p className="text-sm text-gray-500 mb-4">
              Shown on the public Sale Info page. Pre-filled with your standard content — edit as
              needed. Blank fields are omitted from the page.
            </p>
            <div className="space-y-4">
              <div>
                <label className="label">Auction Type</label>
                <input className="input" value={saleInfoForm.auctionType}
                  onChange={(e) => setSI('auctionType', e.target.value)} disabled={importing} />
              </div>
              <div>
                <label className="label">Location</label>
                <input className="input" value={saleInfoForm.location}
                  onChange={(e) => setSI('location', e.target.value)} disabled={importing} />
              </div>
              <div>
                <label className="label">Buyer's Premium</label>
                <input className="input" value={saleInfoForm.buyersPremiumText}
                  onChange={(e) => setSI('buyersPremiumText', e.target.value)} disabled={importing} />
              </div>
              <div>
                <label className="label">Date / Time Info</label>
                <textarea className="input" rows={2} value={saleInfoForm.dateTimeInfo}
                  onChange={(e) => setSI('dateTimeInfo', e.target.value)} disabled={importing} />
              </div>
              <div>
                <label className="label">Preview Info</label>
                <textarea className="input" rows={2} value={saleInfoForm.previewInfo}
                  onChange={(e) => setSI('previewInfo', e.target.value)} disabled={importing} />
              </div>
              <div>
                <label className="label">Checkout Info</label>
                <textarea className="input" rows={2} value={saleInfoForm.checkoutInfo}
                  onChange={(e) => setSI('checkoutInfo', e.target.value)} disabled={importing} />
              </div>
              <div>
                <label className="label">Payment Terms</label>
                <textarea className="input" rows={3} value={saleInfoForm.paymentTerms}
                  onChange={(e) => setSI('paymentTerms', e.target.value)} disabled={importing} />
              </div>
              <div>
                <label className="label">Shipping / Pick Up</label>
                <textarea className="input" rows={3} value={saleInfoForm.shippingPickup}
                  onChange={(e) => setSI('shippingPickup', e.target.value)} disabled={importing} />
              </div>

              {/* Terms & Conditions — repeatable rows (add / remove / reorder) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Terms &amp; Conditions ({terms.length})</label>
                  <button type="button" className="text-sm text-blue-600 hover:underline"
                    onClick={addTerm} disabled={importing}>+ Add term</button>
                </div>
                <div className="space-y-2">
                  {terms.map((t, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <span className="text-xs text-gray-400 mt-2 w-6 text-right">{i + 1}.</span>
                      <textarea className="input flex-1" rows={2} value={t}
                        onChange={(e) => updateTerm(i, e.target.value)} disabled={importing} />
                      <div className="flex flex-col gap-1 text-xs">
                        <button type="button" className="text-gray-400 hover:text-gray-700"
                          onClick={() => moveTerm(i, -1)} disabled={importing || i === 0} title="Move up">▲</button>
                        <button type="button" className="text-gray-400 hover:text-gray-700"
                          onClick={() => moveTerm(i, 1)} disabled={importing || i === terms.length - 1} title="Move down">▼</button>
                        <button type="button" className="text-red-500 hover:text-red-700"
                          onClick={() => removeTerm(i)} disabled={importing} title="Remove">✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="card">
            <h2 className="text-xl font-bold mb-4">3. Preview ({parsedLots.length} lots)</h2>
            
            {imageFolders.length > 0 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-semibold text-blue-800 mb-2">
                  Image Folders Detected:
                </p>
                <div className="flex flex-wrap gap-2">
                  {imageFolders.map(folder => (
                    <span key={folder} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {folder}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-blue-700 mt-2">
                  ℹ️ Images will be matched by SKU when you upload image folders
                </p>
              </div>
            )}
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {parsedLots.slice(0, 10).map((lot, i) => (
                <div key={i} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-600">
                        LOT {lot.lotNumber}
                      </p>
                      <p className="font-bold mt-1">{lot.title}</p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {lot.description}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm text-gray-600">Starting Bid</p>
                      <p className="font-bold text-primary-600">
                        ${(lot.startingBid / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  {lot.notes && (
                    <p className="text-xs text-gray-500 mt-2">
                      📁 Images: {lot.notes}
                    </p>
                  )}
                </div>
              ))}
              
              {parsedLots.length > 10 && (
                <p className="text-center text-gray-600 text-sm">
                  ... and {parsedLots.length - 10} more lots
                </p>
              )}
            </div>
          </div>

          {/* Import Button */}
          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
              disabled={importing}
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              className="btn-primary"
              disabled={importing || !auctionTitle || !startDate || !endDate || (consignorCheck !== null && !consignorCheck.ok)}
            >
              {importing ? 'Creating Auction...' : `Import ${parsedLots.length} Lots`}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ImportCSV;